
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

/**
 * =====================================================
 * AUTH PROVIDER
 * =====================================================
 *
 * Maneja:
 *
 * ✔ Login
 * ✔ Logout
 * ✔ Sesión persistente
 * ✔ Usuario actual
 * ✔ Rol del usuario
 * ✔ Token JWT
 * ✔ Estado de carga
 * ✔ Redirección después de cerrar sesión
 *
 * =====================================================
 */

export function AuthProvider({ children }) {
  const navigate = useNavigate();

  const [auth, setAuthState] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * =====================================================
   * VALIDAR JWT
   * =====================================================
   */

  const validateJWT = useCallback((token) => {
    if (
      !token ||
      typeof token !== "string"
    ) {
      return false;
    }

    try {
      const parts = token.split(".");

      if (parts.length !== 3) {
        return false;
      }

      const payload = JSON.parse(
        atob(
          parts[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/")
        )
      );

      /**
       * Si el JWT tiene fecha de expiración
       * y ya expiró, la sesión no es válida.
       */

      if (
        payload?.exp &&
        payload.exp * 1000 <= Date.now()
      ) {
        return false;
      }

      return true;
    } catch (error) {
      console.warn(
        "AUTH: error validando JWT:",
        error
      );

      return false;
    }
  }, []);

  /**
   * =====================================================
   * LIMPIAR SESIÓN
   * =====================================================
   */

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem("auth");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem(
      "redirectAfterLogin"
    );
  }, []);

  /**
   * =====================================================
   * CARGAR SESIÓN
   * =====================================================
   */

  useEffect(() => {
    let mounted = true;

    const loadSession = () => {
      try {
        const storedAuth =
          localStorage.getItem("auth");

        /**
         * No existe sesión.
         */

        if (!storedAuth) {
          if (mounted) {
            setAuthState(null);
          }

          return;
        }

        /**
         * Convertir JSON.
         */

        const parsed =
          JSON.parse(storedAuth);

        /**
         * Validar estructura.
         */

        if (
          !parsed ||
          !parsed.access ||
          !parsed.user
        ) {
          throw new Error(
            "Invalid auth structure"
          );
        }

        /**
         * Validar JWT.
         */

        if (
          !validateJWT(parsed.access)
        ) {
          console.warn(
            "AUTH: JWT expirado o inválido."
          );

          clearStoredSession();

          if (mounted) {
            setAuthState(null);
          }

          return;
        }

        /**
         * Restaurar sesión.
         */

        if (mounted) {
          setAuthState(parsed);
        }
      } catch (error) {
        console.error(
          "AUTH LOAD ERROR:",
          error
        );

        clearStoredSession();

        if (mounted) {
          setAuthState(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      mounted = false;
    };
  }, [
    clearStoredSession,
    validateJWT,
  ]);

  /**
   * =====================================================
   * LOGIN
   * =====================================================
   */

  const login = useCallback(
    (authData) => {
      /**
       * Validar respuesta.
       */

      if (
        !authData?.access ||
        !authData?.user
      ) {
        console.error(
          "AUTH LOGIN: Invalid login payload"
        );

        return false;
      }

      /**
       * Validar JWT.
       */

      if (
        !validateJWT(authData.access)
      ) {
        console.error(
          "AUTH LOGIN: Invalid or expired JWT"
        );

        return false;
      }

      /**
       * Normalizar información.
       */

      const normalizedAuth = {
        ...authData,
        user: {
          ...authData.user,
        },
      };

      /**
       * Guardar en React.
       */

      setAuthState(normalizedAuth);

      /**
       * Guardar sesión principal.
       */

      localStorage.setItem(
        "auth",
        JSON.stringify(normalizedAuth)
      );

      /**
       * Guardar access token.
       */

      localStorage.setItem(
        "access",
        normalizedAuth.access
      );

      /**
       * Guardar refresh token.
       */

      if (normalizedAuth.refresh) {
        localStorage.setItem(
          "refresh",
          normalizedAuth.refresh
        );
      } else {
        localStorage.removeItem(
          "refresh"
        );
      }

      return true;
    },
    [validateJWT]
  );

  /**
   * =====================================================
   * LOGOUT
   * =====================================================
   *
   * IMPORTANTE:
   *
   * 1. Limpiamos estado React.
   * 2. Limpiamos localStorage.
   * 3. Eliminamos cualquier redirección guardada.
   * 4. Vamos a /login con replace.
   *
   * Esto evita:
   *
   * /admin → /login → /admin → /login
   *
   * =====================================================
   */

  const logout = useCallback(() => {
    console.log(
      "AUTH: cerrando sesión..."
    );

    /**
     * Limpiar estado.
     */

    setAuthState(null);

    /**
     * Limpiar almacenamiento.
     */

    clearStoredSession();

    /**
     * Ir al login.
     *
     * replace evita conservar /admin
     * como destino del historial.
     */

    navigate(
      "/login",
      {
        replace: true,
        state: {
          from: null,
          loggedOut: true,
        },
      }
    );
  }, [
    clearStoredSession,
    navigate,
  ]);

  /**
   * =====================================================
   * ACTUALIZAR USUARIO
   * =====================================================
   */

  const updateUser = useCallback(
    (newUserData) => {
      setAuthState((prev) => {
        if (!prev) {
          return prev;
        }

        const updated = {
          ...prev,
          user: {
            ...prev.user,
            ...newUserData,
          },
        };

        localStorage.setItem(
          "auth",
          JSON.stringify(updated)
        );

        return updated;
      });
    },
    []
  );

  /**
   * =====================================================
   * VALORES DERIVADOS
   * =====================================================
   */

  const user =
    auth?.user ?? null;

  const access =
    auth?.access ?? null;

  const refresh =
    auth?.refresh ?? null;

  const role =
    user?.role ?? null;

  const isAuthenticated =
    Boolean(access) &&
    Boolean(user);

  /**
   * =====================================================
   * CONTEXT VALUE
   * =====================================================
   */

  const value = useMemo(
    () => ({
      auth,
      user,
      access,
      refresh,
      role,
      isAuthenticated,
      loading,
      login,
      logout,
      updateUser,
    }),
    [
      auth,
      user,
      access,
      refresh,
      role,
      isAuthenticated,
      loading,
      login,
      logout,
      updateUser,
    ]
  );

  /**
   * =====================================================
   * PROVIDER
   * =====================================================
   */

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * =====================================================
 * HOOK
 * =====================================================
 */

export function useAuth() {
  const context =
    useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return context;
}
